import React, {FC} from 'react';
import {DragDropContext, Droppable, Draggable} from 'react-beautiful-dnd';
import {useTranslation} from 'react-i18next';
import {Helmet} from 'react-helmet';
import {Container, Button, Badge, Spinner, Input, Label} from 'reactstrap';
import Icon from '@ailibs/feather-react-ts';
import Breadcrumbs from 'components/Common/Breadcrumb';
import useProjectsReorder from './use-projects-reorder';
import {
  ProjectCategoryFilterEnum,
  getProjectCategory,
} from './project-category';
import ReorderSkeleton from './skeleton';
import RowImages from './row-images';
import messages from './messages';
import './index.scss';

const ProjectsReorderPage: FC = () => {
  const {t} = useTranslation();
  const {
    items,
    loading,
    previewItems,
    typeFilter,
    setTypeFilter,
    saving,
    onDragEnd,
    moveToFirst,
    moveToLast,
    handleSave,
    handleCancel,
    orderByItemId,
  } = useProjectsReorder();

  const categoryOptions: Array<{id: ProjectCategoryFilterEnum; name: string}> = [
    {id: ProjectCategoryFilterEnum.INTERIORS, name: t('projects.reorder.categories.interiors')},
    {id: ProjectCategoryFilterEnum.PROJECT_ROOM, name: t('projects.reorder.categories.projectRoom')},
    {id: ProjectCategoryFilterEnum.COLLABORATIONS, name: t('projects.reorder.categories.collaborations')},
  ];

  const isPreview = !!previewItems;
  const displayItems = (previewItems as any[]) || items;
  const filteredItems = displayItems.filter(
    item => !typeFilter || getProjectCategory(item.project_type) === typeFilter
  );

  return (
    <div className="page-content pb-0">
      <Helmet>
        <title>{t(...messages.title())}</title>
      </Helmet>
      <Container fluid>
        <Breadcrumbs
          breadcrumbItems={[
            {title: t('common.dashboard'), path: '/dashboard'},
            {title: t(...messages.title()), path: '#'},
          ]}
        />
        <div className="d-flex align-items-center gap-3 mb-4 flex-wrap">
          <div className="d-flex align-items-center gap-2">
            <Label for="reorder-type-filter" className="mb-0 fw-semibold">
              {t(...messages.filterByType())}
            </Label>
            <Input
              id="reorder-type-filter"
              type="select"
              value={typeFilter || ''}
              onChange={e => setTypeFilter(e.target.value)}
              className="w-auto"
              disabled={isPreview}
            >
              {categoryOptions.map(opt => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </Input>
          </div>
          <Button color="primary" onClick={handleSave} disabled={saving}>
            {saving ? <Spinner size="sm" className="me-1" /> : null}
            {t(...messages.save())}
          </Button>
          <Button color="light" onClick={handleCancel}>
            {t(...messages.cancel())}
          </Button>
        </div>
        {loading ? (
          <ReorderSkeleton />
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="projects-list" direction="vertical">
              {dropProvided => (
                <div
                  ref={dropProvided.innerRef}
                  {...dropProvided.droppableProps}
                  className="reorder-list"
                >
                  {filteredItems.map((item, index) => {
                    const title =
                      item.name?.fr || item.name?.en || '';
                    const subtitle =
                      item.location?.fr ||
                      item.location?.en ||
                      item.project_type ||
                      '';

                    return (
                      <Draggable
                        key={String(item.id)}
                        draggableId={String(item.id)}
                        index={index}
                        isDragDisabled={isPreview}
                      >
                        {(dragProvided, snapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            className={`reorder-row ${
                              snapshot.isDragging
                                ? 'reorder-row--dragging'
                                : ''
                            }`}
                          >
                            <div
                              className="reorder-row__handle"
                              {...dragProvided.dragHandleProps}
                              aria-label="drag handle"
                            >
                              <Icon name="menu" size={18} />
                            </div>
                            <div className="reorder-row__body">
                              <RowImages
                                projectId={item.id}
                                fallbackTitle={title}
                              />
                              <div className="reorder-row__meta">
                                <span className="reorder-row__title">
                                  {title}
                                </span>
                                {subtitle && (
                                  <span className="reorder-row__location">
                                    {subtitle}
                                  </span>
                                )}
                                {item.display_order === null &&
                                  !isPreview && (
                                    <Badge
                                      color="warning"
                                      className="reorder-row__unranked"
                                    >
                                      {t(...messages.unranked())}
                                    </Badge>
                                  )}
                              </div>
                            </div>
                            <div className="reorder-row__actions">
                              <button
                                type="button"
                                className="reorder-row__jump-btn"
                                onClick={() => moveToFirst(String(item.id))}
                                disabled={isPreview || index === 0}
                                title="Move to first"
                              >
                                <Icon name="chevrons-up" size={14} />
                              </button>
                              <div className="reorder-row__order">
                                #{orderByItemId[String(item.id)] ?? index + 1}
                              </div>
                              <button
                                type="button"
                                className="reorder-row__jump-btn"
                                onClick={() => moveToLast(String(item.id))}
                                disabled={isPreview || index === filteredItems.length - 1}
                                title="Move to last"
                              >
                                <Icon name="chevrons-down" size={14} />
                              </button>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {dropProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Container>
    </div>
  );
};

export default ProjectsReorderPage;
